import { Injectable, computed, signal } from '@angular/core';

/**
 * Quanto rumore fa un'attesa.
 *
 * - `silenzioso`: nessun segnale. Per il typeahead e per le chiamate ripetute.
 * - `barra`: barra sottile in cima, la pagina resta usabile.
 * - `bloccante`: velo davanti alla pagina, per le attese volute e lunghe.
 */
export type ModoCaricamento = 'silenzioso' | 'barra' | 'bloccante';

interface Attesa {
  id: number;
  modo: ModoCaricamento;
  messaggio: string | null;
}

/**
 * Stato delle attese in corso.
 *
 * Prima esisteva un solo modo: qualunque chiamata HTTP stendeva un velo nero
 * su tutta l'applicazione, compreso l'autocomplete della categoria mentre si
 * digita. Qui l'attesa dichiara quanto disturbo merita, e ogni voce si chiude
 * da sola: le richieste si sovrappongono e non finiscono nell'ordine in cui
 * sono partite.
 */
@Injectable({
  providedIn: 'root',
})
export class CaricamentoService {
  readonly #attese = signal<Attesa[]>([]);
  #prossimoId = 1;

  /** Barra sottile: accesa da ogni attesa che non sia silenziosa. */
  readonly barraAttiva = computed(() => this.#attese().some((a) => a.modo !== 'silenzioso'));

  /** Velo davanti alla pagina. */
  readonly bloccoAttivo = computed(() => this.#attese().some((a) => a.modo === 'bloccante'));

  /** Testo del velo: quello dell'attesa bloccante aperta piu' di recente. */
  readonly messaggio = computed(() => {
    const bloccanti = this.#attese().filter((a) => a.modo === 'bloccante');
    return bloccanti.length ? bloccanti[bloccanti.length - 1].messaggio : null;
  });

  /** Apre un'attesa e restituisce l'identificativo con cui va chiusa. */
  apri(modo: ModoCaricamento, messaggio: string | null = null): number {
    const id = this.#prossimoId++;
    this.#attese.update((attese) => [...attese, { id, modo, messaggio }]);
    return id;
  }

  chiudi(id: number): void {
    this.#attese.update((attese) => attese.filter((a) => a.id !== id));
  }
}
